/**
 * The origin every page, practice area and auth step lives on.
 *
 * The home site and all practice areas (appIds 1-5) are one site served from
 * one image on juristai.org and nougat.law. A session is stored per origin, so
 * moving a signed-in user to the other domain signs them out; and with
 * everything on one origin no step ever needs to carry a token in a URL.
 * So: on any real host, stay on the origin the visitor is on.
 *
 * Only on a local host (a dev server per app, tests in jsdom) is the site
 * elsewhere: then the configured central site is used (REACT_APP_CENTRAL_AUTH_ORIGIN,
 * REACT_APP_JURIST_BASE_URL or REACT_APP_JURISTAI_BASE_URL), else https://juristai.org.
 */
export const DEFAULT_SITE_ORIGIN = "https://juristai.org";

const LOCAL_HOST = /^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\]|::1)$/i;

export const isLocalHost = (hostname) => LOCAL_HOST.test(String(hostname || "").trim());

const configuredSiteOrigin = () => {
  const env = typeof process !== "undefined" && process.env ? process.env : {};
  const configured =
    env.REACT_APP_CENTRAL_AUTH_ORIGIN || env.REACT_APP_JURIST_BASE_URL || env.REACT_APP_JURISTAI_BASE_URL || "";
  return String(configured || DEFAULT_SITE_ORIGIN).trim().replace(/\/+$/, "");
};

const currentHostname = () => (typeof window !== "undefined" && window.location ? window.location.hostname : "");

/** Origin of the site for a hostname (defaults to the page's own). */
export const siteOriginForHost = (hostname = currentHostname()) => {
  const host = String(hostname || "").trim().toLowerCase();
  if (!host || isLocalHost(host)) return configuredSiteOrigin();
  if (typeof window !== "undefined" && window.location && window.location.hostname.toLowerCase() === host) {
    return window.location.origin;
  }
  return `https://${host}`;
};

export const siteOrigin = () => siteOriginForHost();

/** Absolute URL on the site; absolute inputs are returned unchanged. */
export const siteUrl = (path = "/", hostname = currentHostname()) => {
  if (typeof path === "string" && /^https?:\/\//i.test(path)) return path;
  const normalized = typeof path === "string" && path ? path : "/";
  return `${siteOriginForHost(hostname)}${normalized.startsWith("/") ? normalized : `/${normalized}`}`;
};

/** Hosts that are this site (any domain it is served on), as opposed to third parties. */
export const SITE_HOSTS = new Set(["juristai.org", "www.juristai.org", "nougat.law", "www.nougat.law"]);

/** Retired product domains, mapped to the appId they hosted ("" = several: land on /dashboard). */
export const LEGACY_APP_HOSTS = {
  "fedcrim.ai": "1",
  "www.fedcrim.ai": "1",
  "atticusai.org": "1",
  "www.atticusai.org": "1",
  "litigai.org": "2",
  "www.litigai.org": "2",
  "seriesai.co": "",
  "www.seriesai.co": "",
  "inhouse.juristai.org": "5",
};
